import { useEffect, useMemo, useState } from "react";
import { Plus, Search, Pencil, PackagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, peso } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { NeedTenant } from "../components/layout/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const EMPTY_FORM = {
  name: "", sku_barcode: "", category: "", branch_id: "", cost_price: "", retail_price: "",
  stock_quantity: "0", low_stock_threshold: "5", item_image_url: "", meta: {},
};

function AlertBadges({ p }) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      {p.is_expired && <span data-testid="badge-expired" className="rounded bg-red-100 border border-red-200 px-2 py-0.5 text-[10px] font-bold text-red-700">EXPIRED</span>}
      {!p.is_expired && p.is_expiring_soon && <span data-testid="badge-expiring-soon" className="rounded bg-orange-100 border border-orange-200 px-2 py-0.5 text-[10px] font-bold text-orange-700">EXPIRING &lt;60d</span>}
      {p.is_low_stock && <span data-testid="badge-low-stock" className="rounded bg-amber-100 border border-amber-300 px-2 py-0.5 text-[10px] font-bold text-amber-700">LOW STOCK</span>}
      {!p.is_expired && !p.is_expiring_soon && !p.is_low_stock && <span className="rounded bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold text-emerald-700">OK</span>}
    </div>
  );
}

export default function InventoryPage() {
  const { user, tenant, permissions, activeTenantId } = useAuth();
  const canManageInventory = user.role === "system_admin" || !!permissions.manage_inventory;
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [stockDialog, setStockDialog] = useState(null);
  const [stockDelta, setStockDelta] = useState("");
  const [stockReason, setStockReason] = useState("restock");
  const bt = tenant?.business_type;

  const load = async () => {
    try {
      const [{ data: prods }, { data: brs }] = await Promise.all([api.get("/products"), api.get("/branches")]);
      setProducts(prods);
      setBranches(brs);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => {
    if (user.role === "system_admin" && !activeTenantId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId]);

  const filtered = useMemo(() => products.filter((p) => {
    const q = search.trim().toLowerCase();
    return !q || p.name.toLowerCase().includes(q) || (p.sku_barcode || "").toLowerCase().includes(q);
  }), [products, search]);

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); setFormOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, sku_barcode: p.sku_barcode || "", category: p.category || "",
      branch_id: p.branch_id || "", cost_price: String(p.cost_price), retail_price: String(p.retail_price),
      stock_quantity: String(p.stock_quantity), low_stock_threshold: String(p.low_stock_threshold),
      item_image_url: p.item_image_url || "", meta: p.generic_metadata || {},
    });
    setFormOpen(true);
  };

  const setMeta = (k, v) => setForm({ ...form, meta: { ...form.meta, [k]: v } });

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name, sku_barcode: form.sku_barcode || null, category: form.category || null,
      branch_id: form.branch_id || null, cost_price: Number(form.cost_price), retail_price: Number(form.retail_price),
      stock_quantity: Number(form.stock_quantity) || 0, low_stock_threshold: Number(form.low_stock_threshold) || 0,
      item_image_url: form.item_image_url || null, generic_metadata: form.meta,
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.product_id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      setFormOpen(false);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const adjustStock = async () => {
    try {
      await api.post(`/products/${stockDialog.product_id}/stock`, { delta: Number(stockDelta), reason: stockReason });
      toast.success("Stock adjusted");
      setStockDialog(null); setStockDelta("");
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const doDelete = async (p) => {
    try {
      await api.delete(`/products/${p.product_id}`);
      toast.success("Product deleted");
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  if (user.role === "system_admin" && !activeTenantId) return <NeedTenant />;

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Inventory</h1>
          <p className="text-sm text-slate-500">{products.length} products · {tenant?.store_name}</p>
        </div>
        <div className="flex-1" />
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="inventory-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or SKU…" className="pl-9 w-64 bg-white" />
        </div>
        {canManageInventory ? (
          <Button onClick={openAdd} className="bg-slate-900 hover:bg-slate-800" data-testid="inventory-add-product-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add Product
          </Button>
        ) : (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Inventory management permission not granted to your role.
          </p>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Product</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead className="text-right">Stock</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.product_id} data-testid={`inventory-row-${p.product_id}`}>
                <TableCell>
                  <p className="font-medium text-slate-900">{p.name}</p>
                  <p className="text-xs text-slate-400 capitalize">{p.category || bt}</p>
                </TableCell>
                <TableCell className="font-mono text-xs text-slate-500">{p.sku_barcode || "—"}</TableCell>
                <TableCell className="text-right font-mono text-sm">{peso(p.cost_price)}</TableCell>
                <TableCell className="text-right font-mono text-sm font-semibold">{peso(p.retail_price)}</TableCell>
                <TableCell className="text-right font-mono text-sm">{p.stock_quantity}</TableCell>
                <TableCell><AlertBadges p={p} /></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {canManageInventory && (
                      <>
                        <Button variant="ghost" size="icon" onClick={() => { setStockDialog(p); setStockReason("restock"); }} data-testid={`stock-adjust-${p.product_id}`} title="Adjust stock">
                          <PackagePlus className="h-4 w-4 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-product-${p.product_id}`} title="Edit">
                          <Pencil className="h-4 w-4 text-slate-500" />
                        </Button>
                      </>
                    )}
                    {user.role === "system_admin" && (
                      <Button variant="ghost" size="icon" onClick={() => doDelete(p)} data-testid={`delete-product-${p.product_id}`} title="Delete">
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-sm text-slate-400">No products found.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-form-modal">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Product name</Label>
              <Input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>SKU / Barcode</Label>
                <Input data-testid="product-sku-input" value={form.sku_barcode} onChange={(e) => setForm({ ...form, sku_barcode: e.target.value })} className="font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Beverages" />
              </div>
            </div>
            {branches.length > 1 && (
              <div className="space-y-1.5">
                <Label>Branch</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.branch_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Cost price</Label>
                <Input data-testid="product-cost-input" type="number" step="0.01" min="0" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label>Retail price</Label>
                <Input data-testid="product-price-input" type="number" step="0.01" min="0" value={form.retail_price} onChange={(e) => setForm({ ...form, retail_price: e.target.value })} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Stock quantity</Label>
                <Input data-testid="product-stock-input" type="number" min="0" value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Low stock alert at</Label>
                <Input type="number" min="0" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Image URL <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input value={form.item_image_url} onChange={(e) => setForm({ ...form, item_image_url: e.target.value })} placeholder="https://…" />
            </div>

            {bt === "pharmacy" && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pharmacy Details</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Expiry date</Label>
                    <Input data-testid="product-expiry-date-input" type="date" value={form.meta.expiry_date || ""} onChange={(e) => setMeta("expiry_date", e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Batch number</Label>
                    <Input data-testid="product-batch-number-input" value={form.meta.batch_number || ""} onChange={(e) => setMeta("batch_number", e.target.value)} className="font-mono" />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="rx-toggle">Requires prescription (Rx)</Label>
                  <Switch id="rx-toggle" data-testid="product-rx-required-toggle" checked={!!form.meta.requires_rx} onCheckedChange={(v) => setMeta("requires_rx", v)} />
                </div>
              </div>
            )}
            {bt === "retail" && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Apparel Details</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Size</Label>
                    <Input data-testid="product-size-input" value={form.meta.size || ""} onChange={(e) => setMeta("size", e.target.value)} placeholder="XS / S / M / L / XL" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Color</Label>
                    <Input data-testid="product-color-input" value={form.meta.color || ""} onChange={(e) => setMeta("color", e.target.value)} />
                  </div>
                </div>
              </div>
            )}
            {(bt === "grocery" || bt === "cafe") && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Grocery Details</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Unit of measure</Label>
                    <Select value={form.meta.unit_of_measure || "pcs"} onValueChange={(v) => setMeta("unit_of_measure", v)}>
                      <SelectTrigger data-testid="product-uom-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["pcs", "kg", "g", "L", "ml", "pack"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between pt-6">
                    <Label htmlFor="weighted-toggle">Weighted item</Label>
                    <Switch id="weighted-toggle" checked={!!form.meta.is_weighted} onCheckedChange={(v) => setMeta("is_weighted", v)} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Expiry date <span className="text-slate-400 font-normal">(optional)</span></Label>
                  <Input type="date" value={form.meta.expiry_date || ""} onChange={(e) => setMeta("expiry_date", e.target.value)} />
                </div>
              </div>
            )}

            <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="product-save-btn">
              {editing ? "Save changes" : "Add product"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!stockDialog} onOpenChange={(o) => !o && setStockDialog(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="font-heading">Adjust Stock — {stockDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Current: <span className="font-mono font-semibold text-slate-900">{stockDialog?.stock_quantity}</span></p>
            <div className="space-y-1.5">
              <Label>Adjustment (+ add / − remove)</Label>
              <Input type="number" value={stockDelta} onChange={(e) => setStockDelta(e.target.value)} placeholder="e.g. 24 or -3" data-testid="stock-delta-input" />
            </div>
            <Select value={stockReason} onValueChange={setStockReason}>
              <SelectTrigger data-testid="stock-reason-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="restock">Restock</SelectItem>
                <SelectItem value="wastage">Wastage / Spoilage</SelectItem>
                <SelectItem value="damaged">Damaged write-off</SelectItem>
                <SelectItem value="correction">Count correction</SelectItem>
              </SelectContent>
            </Select>
            <Button className="w-full bg-slate-900" onClick={adjustStock} disabled={!stockDelta || Number(stockDelta) === 0} data-testid="stock-adjust-save-btn">
              Apply adjustment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
